/* ==========================================================================
   Kenza Pro — Motion utilities (opt-in helpers, no auto-init)
   Exposed on window.KP for use by section-specific scripts.
   ========================================================================== */

(() => {
  window.KP = window.KP || {};

  // ---------- splitTextIntoWords ----------
  // Wraps each non-whitespace text run inside an element in <span class="kp-word">,
  // preserving any inline tags (<em>, <i>, <strong>, <a>, etc.).
  window.KP.splitWords = (el) => {
    if (!el || el.dataset.kpSplit === 'done') return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const textNodes = [];
    let n;
    while ((n = walker.nextNode())) textNodes.push(n);
    textNodes.forEach(node => {
      const parts = node.textContent.split(/(\s+)/);
      const frag = document.createDocumentFragment();
      parts.forEach(p => {
        if (p.trim() === '') {
          frag.appendChild(document.createTextNode(p));
        } else {
          const span = document.createElement('span');
          span.className = 'kp-word';
          span.textContent = p;
          frag.appendChild(span);
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.dataset.kpSplit = 'done';
  };

  // ---------- splitTextIntoLines ----------
  // Measures word positions and wraps consecutive words on the same baseline
  // into a single .kp-line span. Robust to font/responsive changes via re-measure.
  window.KP.splitLines = (el) => {
    if (!el || el.dataset.kpLinesSplit === 'done') return;
    window.KP.splitWords(el);
    const words = Array.from(el.querySelectorAll('.kp-word'));
    if (!words.length) return;
    const lines = [];
    let currentLine = [];
    let currentTop = null;
    words.forEach(w => {
      const top = w.getBoundingClientRect().top;
      if (currentTop === null || Math.abs(top - currentTop) < 4) {
        currentLine.push(w);
        currentTop = top;
      } else {
        lines.push(currentLine);
        currentLine = [w];
        currentTop = top;
      }
    });
    if (currentLine.length) lines.push(currentLine);
    // Wrap each line in an overflow:hidden span so words translate from below.
    lines.forEach(line => {
      const wrap = document.createElement('span');
      wrap.className = 'kp-line';
      const first = line[0];
      first.parentNode.insertBefore(wrap, first);
      line.forEach(w => wrap.appendChild(w));
    });
    el.dataset.kpLinesSplit = 'done';
  };

  // ---------- countUp ----------
  // Animates the integer in `el.textContent` from 0 to its parsed target.
  // Preserves non-numeric suffix (e.g., "4.9★", "500+", "8000+").
  window.KP.countUp = (el, opts = {}) => {
    if (!el) return;
    const raw = (opts.target ?? el.dataset.kpCounter ?? el.textContent).toString().trim();
    const match = raw.match(/^([0-9.,]+)(.*)$/);
    if (!match) return;
    const target = parseFloat(match[1].replace(/[.,]/g, ''));
    const suffix = match[2] || '';
    if (isNaN(target)) return;
    const duration = opts.duration ?? Math.min(2000, 600 + target * 0.05);
    const ease = opts.ease ?? (t => 1 - Math.pow(1 - t, 3));
    const fmt = opts.format ?? (v => v.toLocaleString('nl-NL') + suffix);
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      el.textContent = fmt(Math.round(target * ease(t)));
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  // ---------- kenBurns (manual trigger — for sections that want to opt-in) ----------
  // Adds the .kp-ken-burns class which runs the slow zoom/pan keyframe loop.
  window.KP.kenBurns = (el) => {
    if (!el) return;
    el.classList.add('kp-ken-burns');
  };
})();
